/** Creator-only Drive folder control. Google Picker selects; the server validates and stores the folder. */
import { createPortal } from 'react-dom';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { IconExternalLink, IconFolder, IconLoader2, IconRefresh } from '@tabler/icons-react';
import { FieldCardSettingsContext } from './FieldCardSettingsContext';
import { openGoogleFolderPicker, type FolderPickResult, type OpenFolderPicker } from './googleFolderPicker';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { Select } from '../ui/Select';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { ButtonContent } from '../ui/ButtonContent';
import { GoogleDriveIcon } from '../icons/GoogleDriveIcon';
import type { StorageDestination } from '../../lib/integrations/storage/types';
import type { GooglePickerSession } from '../../lib/integrations/storage/picker-config';

/** Public connection metadata available through organization RLS. */
interface StorageAccount { id: string; account_email: string; status: string }
/** A conversation shares one destination across every file question. */
export interface StorageDestinationPickerProps {
  organizationId: string;
  value?: StorageDestination;
  onChange: (value: StorageDestination) => void;
  children?: React.ReactNode;
  /** Opens one Google Picker session; injectable so tests and fixtures never load Google scripts. */
  openFolderPicker?: OpenFolderPicker;
}
/** The next step after a failed attempt: retry, pick another folder, or reconnect the account. */
type PickError = 'open' | 'save' | 'repick' | 'reconnect';

const SETTINGS_URL = '/admin/settings?section=integrations';
const ERROR_COPY: Record<Exclude<PickError, 'reconnect'>, string> = {
  open: 'Couldn’t open Drive.',
  save: 'Couldn’t save folder.',
  repick: 'Choose another folder.',
};

/** Preserves the saved selection until a replacement passes server validation. */
export function StorageDestinationPicker({ organizationId, value, onChange, children, openFolderPicker = openGoogleFolderPicker }: StorageDestinationPickerProps): React.ReactElement {
  const { supabase, user } = useSupabase();
  const currentUserId = user?.id;
  const settingsContainer = useContext(FieldCardSettingsContext);
  const editing = settingsContainer !== null;
  const [accounts, setAccounts] = useState<StorageAccount[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string>();
  const [chosenAccountId, setChosenAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PickError>();
  const retry = useRef<(() => Promise<void>) | null>(null);
  const generation = useRef(0);
  const accountGeneration = useRef(0);
  const contextRef = useRef(organizationId);
  contextRef.current = organizationId;
  const activeAccounts = accounts.filter(account => account.status === 'active');
  // Derived, not synced by an effect, so the control is usable on the same render the accounts arrive.
  const accountId = activeAccounts.some(account => account.id === chosenAccountId) ? chosenAccountId
    : activeAccounts.find(account => account.id === value?.connectionId)?.id ?? (activeAccounts.length === 1 ? activeAccounts[0].id : '');

  /** Authenticates every operation using the current holito session. */
  const headers = useCallback(async (): Promise<Record<string, string>> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Session unavailable');
    return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
  }, [supabase]);

  /** Refreshes connection metadata without interrupting a saved destination. */
  const loadAccounts = useCallback(async (): Promise<void> => {
    const context = organizationId;
    const request = ++accountGeneration.current;
    setAccountsLoading(true); setAccountsError(undefined);
    try {
      if (!currentUserId) { setAccounts([]); return; }
      const result = await supabase.from('integration_connections').select('id, account_email, status').eq('organization_id', organizationId).eq('integration_type', 'storage').eq('provider', 'google').eq('user_id', currentUserId);
      if (result.error) throw new Error(result.error.message);
      if (contextRef.current === context && request === accountGeneration.current) setAccounts((result.data ?? []) as StorageAccount[]);
    } catch (cause) {
      if (contextRef.current === context && request === accountGeneration.current) {
        captureException(cause as Error, 'StorageDestinationPicker.loadAccounts', { organizationId });
        setAccountsError('Couldn’t load Drive accounts.');
      }
    } finally {
      if (contextRef.current === context && request === accountGeneration.current) { setAccountsLoading(false); setAccountsLoaded(true); }
    }
  }, [organizationId, supabase, currentUserId]);

  useEffect(() => {
    generation.current += 1;
    setAccountsLoaded(false); setAccounts([]); setChosenAccountId(''); setError(undefined); setBusy(false); retry.current = null;
    void loadAccounts();
    const refresh = () => { void loadAccounts(); };
    window.addEventListener('focus', refresh);
    return () => { generation.current += 1; accountGeneration.current += 1; window.removeEventListener('focus', refresh); };
  }, [loadAccounts]);

  /** Commits only a server-validated writable folder; the saved value is untouched otherwise. */
  const save = async (connectionId: string, folderId: string, request: number): Promise<void> => {
    const context = organizationId;
    const current = () => request === generation.current && contextRef.current === context;
    setBusy(true); setError(undefined); retry.current = null;
    let status: number | undefined;
    try {
      const response = await fetch('/api/integrations/storage/google/destination', { method: 'POST', headers: await headers(), body: JSON.stringify({ organizationId, connectionId, folderId }) });
      status = response.status;
      if (!response.ok) throw new Error(`Destination check failed: ${status}`);
      const data = await response.json() as { destination: StorageDestination };
      if (current()) onChange(data.destination);
    } catch (cause) {
      if (!current()) return;
      // Unwritable or no longer reachable (lost per-file access) folders need a new pick, not a retry.
      if (status === 400 || status === 404) { setError('repick'); return; }
      if (status === 409) { setError('reconnect'); return; }
      if (!status || status >= 500) captureException(cause as Error, 'StorageDestinationPicker.select', { organizationId, connectionId, folderId });
      setError('save'); retry.current = () => save(connectionId, folderId, ++generation.current);
    } finally { if (current()) setBusy(false); }
  };

  /** Mints a fresh owner session for every Picker, so an expired token is never reused. */
  const pick = async (): Promise<void> => {
    const connectionId = accountId;
    if (!connectionId) return;
    const request = ++generation.current;
    const context = organizationId;
    const current = () => request === generation.current && contextRef.current === context;
    setBusy(true); setError(undefined); retry.current = null;
    let status: number | undefined;
    let result: FolderPickResult;
    try {
      const query = new URLSearchParams({ organizationId, connectionId });
      const response = await fetch(`/api/integrations/storage/google/picker?${query}`, { headers: await headers() });
      status = response.status;
      if (!response.ok) throw new Error(`Picker session failed: ${status}`);
      const session = await response.json() as GooglePickerSession;
      if (!current()) return;
      result = await openFolderPicker(session);
    } catch (cause) {
      if (!current()) return;
      setBusy(false);
      if (status === 409) { setError('reconnect'); return; }
      // Network, server and Google script failures are unexpected; other client errors are not.
      if (!status || status < 300 || status >= 500) captureException(cause as Error, 'StorageDestinationPicker.openPicker', { organizationId, connectionId });
      setError('open'); retry.current = pick;
      return;
    }
    if (!current()) return;
    if (result.action === 'picked') { await save(connectionId, result.folderId, request); return; }
    setBusy(false);
    if (result.action === 'error') {
      captureException(new Error('Google Picker reported an error'), 'StorageDestinationPicker.openPicker', { organizationId, connectionId });
      setError('open'); retry.current = pick;
    }
  };

  const message = accountsError ?? (error && error !== 'reconnect' ? ERROR_COPY[error] : undefined);
  const canRetry = Boolean(accountsError) || error === 'open' || error === 'save';
  const controls = <div className="min-w-0 text-sm">
    <AnimatedPresence show={Boolean(value)}><div className="pb-3">{value && <div className="flex min-w-0 items-center gap-2 rounded-lg border border-border bg-background p-2">
      <a aria-label={`Open ${value.folderName} in Drive`} href={`https://drive.google.com/drive/folders/${encodeURIComponent(value.folderId)}`} target="_blank" rel="noopener noreferrer" className="flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-1 text-foreground hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring" title={value.folderPath}>
        <GoogleDriveIcon className="h-4 w-4 shrink-0" /><span className="min-w-0 truncate">{value.folderName}</span><IconExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </a>
      {activeAccounts.length > 0 && <button type="button" disabled={busy || !accountId} aria-busy={busy} aria-label="Change folder" className="min-h-9 shrink-0 rounded-md px-3 text-xs font-medium text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring" onClick={() => void pick()}><ButtonContent loading={busy}>Change</ButtonContent></button>}
    </div>}</div></AnimatedPresence>
    <AnimatedPresence show={!accountsLoaded}><div className="pb-3"><IconLoader2 aria-label="Working" className="h-4 w-4 animate-spin motion-reduce:animate-none" /></div></AnimatedPresence>
    <AnimatedPresence show={accountsLoaded && activeAccounts.length === 0 && !accountsError}><div className="pb-3"><div className="rounded-lg border border-dashed border-border px-3 py-2 text-sm"><a href={SETTINGS_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-volt-soft hover:text-volt">Add Drive</a></div></div></AnimatedPresence>
    <AnimatedPresence show={activeAccounts.length > 0}>
      <div className="flex items-center gap-3 pb-3">
        {activeAccounts.length === 1 ? <div className="flex min-w-0 flex-1 items-center gap-2 text-muted-foreground"><GoogleDriveIcon className="h-4 w-4 shrink-0" /><span className="truncate">{activeAccounts[0].account_email}</span></div>
          : <div className="min-w-0 flex-1"><Select value={accountId} disabled={busy} placeholder="Select Drive" options={activeAccounts.map(account => ({ value: account.id, label: account.account_email, icon: <GoogleDriveIcon className="h-4 w-4" /> }))} onChange={id => { setChosenAccountId(id); setError(undefined); retry.current = null; }} /></div>}
        <button type="button" aria-label="Refresh accounts" aria-busy={accountsLoading} disabled={accountsLoading || busy} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring" onClick={() => void loadAccounts()}><ButtonContent loading={accountsLoading}><IconRefresh aria-hidden="true" className="h-4 w-4" /></ButtonContent></button>
      </div>
      <AnimatedPresence show={!value}><div className="pb-3"><button type="button" disabled={busy || !accountId} aria-busy={busy} className="flex min-h-10 items-center gap-2 self-start rounded-lg border border-border px-3 font-medium text-foreground hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50" onClick={() => void pick()}><ButtonContent loading={busy}><IconFolder aria-hidden="true" className="h-4 w-4" />Choose folder</ButtonContent></button></div></AnimatedPresence>
    </AnimatedPresence>
    <AnimatedPresence show={error === 'reconnect'}><div role="alert" className="pb-3 text-danger"><a href={SETTINGS_URL} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-4">Reconnect Drive</a></div></AnimatedPresence>
    <AnimatedPresence show={Boolean(message)}><div role="alert" className="flex items-center gap-3 pb-3 text-danger"><AnimatedLabel text={message ?? ''} multiline />{canRetry && <button type="button" disabled={accountsLoading || busy} className="shrink-0 underline underline-offset-4" onClick={() => void (accountsError ? loadAccounts() : retry.current?.())}>Retry</button>}</div></AnimatedPresence>
  </div>;

  return <>
    {value && !editing && <div className="flex items-center gap-2 pr-8 text-sm text-muted-foreground"><GoogleDriveIcon className="h-4 w-4 shrink-0" /><span className="min-w-0 truncate" title={value.folderPath}>{value.folderName}</span></div>}
    {editing ? settingsContainer ? createPortal(controls, settingsContainer) : controls
      : !value && activeAccounts.length === 0 ? controls : null}
    {(value || activeAccounts.length > 0) && <div className={value ? 'mt-3' : undefined}>{children}</div>}
  </>;
}
